/**
 * Portal mode resolution.
 *
 * The SP portal renders one of three shells. Which one is decided ONCE, here,
 * and is consumed only by Sidebar.vue (menu contents) and Dashboard.vue (which
 * component to mount). No screen component should import this file — screens
 * are mounted per mode, so they never need to ask which mode they are in.
 *
 * NOTE on isIndigo: `localStorage` stores strings, so the legacy `$isIndigo`
 * holds the string "false" for non-Indigo partners, which is truthy. This file
 * deliberately compares against 'true' (the same way Sidebar.vue already does)
 * so Indigo behaviour is unchanged. The legacy `$isIndigo` global is left
 * exactly as-is elsewhere.
 */

export const MODE = {
  DEFAULT: 'default',
  INDIGO: 'indigo',
  UBER: 'uber',
};

/**
 * Uber wins over Indigo if both flags are somehow set. The backend should
 * refuse to enable both on one service partner, but this keeps the UI
 * deterministic if it ever happens.
 */
export function resolveMode() {
  if (localStorage.getItem('uberFlow') === 'true') return MODE.UBER;
  if (localStorage.getItem('isIndigo') === 'true') return MODE.INDIGO;
  return MODE.DEFAULT;
}

export function isUberMode() {
  return resolveMode() === MODE.UBER;
}

/**
 * Sub-tab ids for the Uber trips shell. Sidebar entries map onto these so a
 * menu click opens the shell at the right tab.
 */
export const UBER_TAB = {
  CREATE: 'tab-uber-create',
  ONGOING: 'tab-uber-ongoing',
  SCHEDULED: 'tab-uber-scheduled',
  ATTENTION: 'tab-uber-attention',
  COMPLETED: 'tab-uber-completed',
  CANCELLED: 'tab-uber-cancelled',
};

/**
 * Menu definition per mode.
 *
 * `default` and `indigo` are the exact lists that were previously hardcoded in
 * Sidebar.vue — moved here unchanged so existing partners see no difference.
 */
export const MENU = {
  [MODE.DEFAULT]: {
    trips: [
      { title: 'Trip History' },
      { title: 'To be Assigned' },
      { title: 'Cancelled Trips' },
    ],
    transport: [{ title: 'Driver Management' }, { title: 'Vehicle Management' }],
    reports: true,
    admin: true,
    billing: false,
    profile: true,
    branchLabel: 'Branch',
    defaultTab: 'To be Assigned',
  },

  [MODE.INDIGO]: {
    trips: [
      { title: 'Trip History' },
      { title: 'To be Approved' },
      { title: 'To be Assigned' },
      { title: 'Last Minute Pullout' },
      { title: 'Cancellation Pending' },
      { title: 'Cancelled Trips' },
      // { title: "Error Trips" },  // kept disabled, as before
    ],
    transport: [{ title: 'Driver Management' }, { title: 'Vehicle Management' }],
    reports: true,
    admin: true,
    billing: true,
    profile: true,
    branchLabel: 'Station',
    defaultTab: 'To be Approved',
  },

  [MODE.UBER]: {
    trips: [
      { title: 'To be Booked', uberTab: UBER_TAB.CREATE },
      { title: 'Attention Required', uberTab: UBER_TAB.ATTENTION },
      { title: 'Scheduled Trips', uberTab: UBER_TAB.SCHEDULED },
      { title: 'Ongoing Trips', uberTab: UBER_TAB.ONGOING },
      { title: 'Completed Trips', uberTab: UBER_TAB.COMPLETED },
      { title: 'Cancelled Trips', uberTab: UBER_TAB.CANCELLED },
    ],
    // Uber supplies the driver and the vehicle, so the Transport menu is hidden.
    transport: [],
    reports: true,
    admin: true,
    billing: false,
    profile: true,
    branchLabel: 'Branch',
    defaultTab: 'To be Booked',
  },
};

export function menuForMode(mode) {
  return MENU[mode] || MENU[MODE.DEFAULT];
}

/**
 * Every tab title reachable in the given mode. Sidebar validates the
 * `selectedTab` it restores from localStorage against this, so a tab saved
 * under a previous mode cannot resurrect a screen the current mode hides.
 */
export function allowedTabs(mode) {
  const menu = menuForMode(mode);
  const tabs = [
    ...menu.trips.map((item) => item.title),
    ...menu.transport.map((item) => item.title),
  ];
  if (menu.reports) tabs.push('Reports');
  if (menu.admin) tabs.push('Admin');
  if (menu.billing) tabs.push('Billing');
  if (menu.profile) tabs.push('Account');
  return tabs;
}

/**
 * Maps a Uber sidebar title onto the sub-tab the shell should open at.
 */
export function uberTabForTitle(title) {
  const found = MENU[MODE.UBER].trips.find((item) => item.title === title);
  return found ? found.uberTab : UBER_TAB.CREATE;
}

/**
 * True only for the titles the Uber trips shell owns. Reports, Admin and
 * Profile are NOT trip tabs - without this check the shell would render
 * alongside them instead of being replaced by them.
 */
export function isUberTripTab(title) {
  return MENU[MODE.UBER].trips.some((item) => item.title === title);
}
